import type { ReactNode } from 'react';

// ============================================================================
// lib/widgets/registry.ts — РЕЕСТР ВИДЖЕТОВ (Рабочие столы + Движок страниц).
// Ядро определяет типы виджетов, которые КЛИЕНТ умеет рендерить (SSoT).
// props JSON хранится в макете страницы; рендер получает props + контекст.
// Неизвестный тип виджета → ЯВНАЯ ошибка (без тихих фоллбэков).
// Конкретные бизнес-виджеты — поставка/пользователь (расширение реестра).
// ============================================================================

/** Контекст виджета (зависит от места рендера). */
export interface WidgetContext {
  /** id текущего пользователя (для «Мои задачи» и т.п.) */
  userId?: string;
  /** Код страницы (для заголовков/навигации) */
  pageCode?: string;
  /** Внешний переход по объекту (публикация — state-based; иначе роут) */
  onObjectOpen?: (objectType: string, objectName: string, objectId?: string) => void;
  /** Внешний переход на РАЗДЕЛ Предприятия (публикация — state-based;
   *  иначе роут /designer/section/{id}). Разделы — единый источник видимости. */
  onSectionOpen?: (sectionId: string) => void;
  /** Создание НОВОЙ записи объекта (документ/заявка/задача/справочник) —
   *  открыть форму создания (публикация — state-based; иначе модалкой/роутом).
   *  Отсутствует — виджет показывает создание через onObjectOpen (список). */
  onObjectCreate?: (objectType: string, objectName: string) => void;
  /** Задать параметр КОНТЕКСТА рабочего стола (master→slave) */
  setContextParam?: (name: string, value: string) => void;
  /** Текущие параметры контекста рабочего стола (для превью/отладки) */
  contextParams?: Record<string, string>;
  /** Сайт (конструктор сайтов): код сайта/локаль — для ссылок и t() блоков */
  siteCode?: string;
  siteLocale?: string;
  siteDefaultLocale?: string;
  /** Сайт (Этап 2): данные записи страницы объекта (GetPublicPage.record) —
   *  биндинг значений блоков {field:"column_name"} (SSoT — сервер). */
  record?: Record<string, string>;
  /** Сайт (Этап 3): словарь переводов локали {ключ: значение} (GetTranslations);
   *  используется блоками как fallback `site.{code}.{ключ}`. */
  translations?: Record<string, string>;
  /** Сайт (хвосты Этапов 2–3): страница-СПИСОК объекта (object_mode='list') —
   *  серверный постраничный результат GetPublicPage (records/total/page_size).
   *  Блок `list` с props.pageRecords рендерит ЭТИ данные (SSoT — сервер). */
  pageList?: PageListContext;
}

/** PageListContext — постраничный список записей list-страницы объекта (SSoT сервера). */
export interface PageListContext {
  items: Array<{ ref: string; values: Record<string, string> }>;
  total: number;
  page: number;
  pageSize: number;
  /** Префикс URL записей (slug_prefix страницы). */
  slugPrefix: string;
  /** URL записи списка (по умолчанию /p/{site}[/{locale}]/{slug_prefix}/{ref}). */
  itemHref: (ref: string) => string;
  /** URL страницы списка с ?page=N (сохраняет query-отборы). */
  pageHref: (page: number) => string;
}

/** Пропсы виджета (из макета; все опциональные). */
export interface WidgetProps {
  /** Заголовок виджета (если виджет его выводит) */
  title?: string;
  /** Для list: тип объекта + имя (catalog/document/task/order/register/poll).
   *  poll — тест/анкета: рендерится ЕДИНЫЙ компонент теста (вопросы+«Пройти»+
   *  протоколы), objectName пуст → список методик. */
  objectType?: string;
  objectName?: string;
  /** Для list: hydraql-запрос (query-список) */
  query?: string;
  /** Для list: отборы [{field, condition, value}] (или JSON-строка из редактора) */
  filters?: Array<{ field: string; condition: string; value?: string }> | string;
  /** Для list: вид (list/cards/tree/flat) */
  viewMode?: 'list' | 'cards' | 'tree' | 'flat';
  /** Для list: число элементов */
  limit?: number;
  /** Для list: панель команд (по умолчанию true) */
  showToolbar?: boolean;
  /** Для list (сайты): рендерить записи СТРАНИЦЫ-СПИСКА (ctx.pageList, серверная
   *  пагинация) вместо собственного источника — та же карточка/строка ведёт на
   *  item-страницу. Собственный источник блока используется как обычно. */
  pageRecords?: boolean;
  /** Для list (сайты): whitelist полей отбора из query-параметров (CSV column_name;
   *  пусто = отборы из URL не применяются). Неизвестные параметры игнорируются. */
  queryFields?: string;
  /** Для list (сайты): URL-шаблон записи ({ref}/{site}/{locale}/{slug_prefix};
   *  пусто — /p/{site}[/{locale}]/{slug_prefix}/{ref}). */
  itemUrl?: string;
  /** Для list: список команд (create/refresh/delete/search/filter; пусто = все) */
  commands?: string[];
  /** Для list: параметр КОНТЕКСТА, куда класть выбранную строку (master→slave) */
  contextParam?: string;
  /** Для list: поле строки для параметра контекста (по умолчанию 'id') */
  contextField?: string;
  /** Параметры ВИДЖЕТА (локальны для виджета): {имя: значение}, в отборах @param.<имя> */
  params?: Record<string, string>;
  /** Для list: колонки (имена) — если заданы */
  columns?: string[];
  /** Для text/html: содержимое */
  text?: string;
  /** Для quick_actions: команды (массив ИЛИ текст «label;команда» построчно) */
  actions?: Array<{ label: string; command: string; variant?: 'primary' | 'outline' | 'danger' }> | string;
  // --- Общие свойства виджета (как элементы групп в редакторе форм) ----------
  /** Растягивать по ширине (по умолчанию true) */
  stretchW?: boolean;
  /** Растягивать по высоте (по умолчанию true) */
  stretchH?: boolean;
  /** Прижать (как в редакторе форм): '' | 'top' | 'bottom' | 'left' | 'right' */
  placement?: '' | 'top' | 'bottom' | 'left' | 'right';
  /** Показывать заголовок (по умолчанию true) */
  showTitle?: boolean;
  /** Показывать рамку (по умолчанию true) */
  showBorder?: boolean;
  /** Произвольные (запас) */
  [key: string]: unknown;
}

export interface WidgetDef {
  type: string;
  display: string;
  description?: string;
  /** Публичная страница: виджет доступен анониму? (иначе не рендерится на /p/{slug}) */
  publicOk?: boolean;
  /** Блок сайта (конструктор сайтов): показывается в палитре сайта и рендерится
   *  с appearance/локализацией. publicOk-виджеты (html/list) тоже доступны. */
  siteOk?: boolean;
  /** Остров: блок сам управляет фоном (hero и т.п.) — appearance-обёртка не режет */
  island?: boolean;
  /** Варианты оформления блока (вариант выбирается в свойствах) */
  variants?: Array<{ value: string; label: string }>;
  /** Схема appearance (единый набор для блоков сайта: фон/отступы/выравнивание) */
  appearanceSchema?: boolean;
  /** Схема props для редактора (динамические свойства виджета) */
  propsSchema?: Array<{
    key: string;
    label: string;
    type: 'text' | 'number' | 'select' | 'boolean' | 'filters' | 'objects' | 'params' | 'multi' | 'textarea' | 'image';
    options?: Array<{ value: string; label: string }>;
    placeholder?: string;
    help?: string;
  }>;
  render: (props: WidgetProps, ctx: WidgetContext) => ReactNode;
}

const registry = new Map<string, WidgetDef>();

/** registerWidget — зарегистрировать виджет (поставки/пользовательские мини-приложения). */
export function registerWidget(def: WidgetDef): void {
  if (!def?.type) throw new Error('widget: type is required');
  registry.set(def.type, def);
}

/** getWidget — виджет по типу; неизвестный → null (рендерер покажет явную ошибку). */
export function getWidget(type: string): WidgetDef | null {
  return registry.get(type) || null;
}

/** listWidgets — все зарегистрированные виджеты (для палитры редактора). */
export function listWidgets(): WidgetDef[] {
  return Array.from(registry.values());
}

export { registry };
